import { getAccessToken } from "@/lib/api/client";

/**
 * Object URLs for files behind the authenticated endpoint.
 *
 * A stored file never changes — a new upload gets a new id — so a URL for one
 * is good for as long as the tab lives. Without this every avatar in a list of
 * twenty refetched its own bytes on each mount, and the faces popped in one by
 * one every time somebody came back to the page.
 *
 * Requests in flight are shared rather than repeated: a list usually asks for
 * the same face several times in the same tick.
 */
const cache = new Map<string, string>();
const inFlight = new Map<string, Promise<string>>();
/**
 * When each failed file last failed. A failure is remembered for a short
 * while, so a broken image in a list of twenty is not asked for twenty times
 * a second, and then forgotten. Remembering it for good meant a file asked
 * for a moment before it was saved, such as a signature just uploaded, never
 * showed until the page was reloaded.
 */
const failed = new Map<string, number>();
const RETRY_AFTER_MS = 15_000;

export function cachedFile(fileId: string): string | null {
  return cache.get(fileId) ?? null;
}

export function fileFailed(fileId: string): boolean {
  const at = failed.get(fileId);
  if (at === undefined) return false;
  if (Date.now() - at < RETRY_AFTER_MS) return true;
  failed.delete(fileId);
  return false;
}

/** Forgets one file, so the next request for it goes to the server again. */
export function forgetFile(fileId: string): void {
  const url = cache.get(fileId);
  if (url) URL.revokeObjectURL(url);
  cache.delete(fileId);
  failed.delete(fileId);
}

export function loadFile(fileId: string): Promise<string> {
  const ready = cache.get(fileId);
  if (ready) return Promise.resolve(ready);

  const existing = inFlight.get(fileId);
  if (existing) return existing;
  failed.delete(fileId);

  const request = (async () => {
    const response = await fetch(`/api/v1/files/${fileId}`, {
      headers: { Authorization: `Bearer ${getAccessToken() ?? ""}` },
      credentials: "include",
    });
    if (!response.ok) throw new Error(String(response.status));
    const blob = await response.blob();
    if (!blob.type.startsWith("image/")) throw new Error("not an image");
    // Never revoked while cached: the URL is the cache entry, and it costs a
    // pointer. clearImageCache releases the lot when somebody signs out.
    const url = URL.createObjectURL(blob);
    cache.set(fileId, url);
    return url;
  })()
    .catch((error: unknown) => {
      failed.set(fileId, Date.now());
      throw error;
    })
    .finally(() => inFlight.delete(fileId));

  inFlight.set(fileId, request);
  return request;
}

/** Drops every cached image. Called on sign-out, so the next person sees none. */
export function clearImageCache(): void {
  for (const url of cache.values()) URL.revokeObjectURL(url);
  cache.clear();
  inFlight.clear();
  failed.clear();
}
